import { ArrowRight, Megaphone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CREATE_PAGE_PATH, PANEL_GRADIENT } from './publicShell'

/**
 * O convite para a escola entrar no Explore.
 *
 * Mora num componente só — usado na landing e no fim da lista do Explore —
 * porque é a mesma promessa nos dois lugares. Copiado, um lado prometeria
 * "anunciamos para você" e o outro "cadastre-se", que é como uma promessa vira
 * duas.
 */
export const CREATE_PAGE_LABEL = 'Criar sua página'

/**
 * Link do botão. Função e não constante só para o texto ficar ao lado do resto.
 *
 * Leva para `CREATE_PAGE_PATH` — o cadastro de conta do criador de página
 * (login/criar conta, verificação de e-mail, painel). A antiga página "em
 * construção" com saída pro WhatsApp não existe mais.
 */
export function createPageHref(): string {
  return CREATE_PAGE_PATH
}

interface CreateYourPageButtonProps {
  /**
   * `solid` no fundo claro (landing), `outline` quando dividir a linha com outro
   * botão cheio e não puder competir com ele.
   */
  variant?: 'solid' | 'outline'
  className?: string
}

/** Só o botão, para quem já tem o texto em volta. */
export function CreateYourPageButton({
  variant = 'solid',
  className,
}: CreateYourPageButtonProps) {
  return (
    <a
      href={createPageHref()}
      className={cn(
        'inline-flex w-fit items-center gap-2 rounded-full px-7 py-3.5 text-[0.9375rem] font-semibold transition',
        variant === 'solid'
          ? 'bg-lp-blue text-white hover:opacity-90'
          : 'border border-lp-blue bg-white text-lp-blue hover:bg-lp-blue/5',
        className,
      )}
    >
      {CREATE_PAGE_LABEL}
      <ArrowRight className="size-4" aria-hidden="true" />
    </a>
  )
}

/**
 * As 7 categorias do Explore. Espelham `School.business_categories` e as keys de
 * ExploreCategory — dois testes de Ruby leem ESTE arquivo e falham se as listas
 * divergirem, por isso o tipo mora aqui e não em types/publicSchool.ts.
 *
 * A categoria é a divisão. O que o negócio FAZ (Surf, Pousadas, Shapers) é o
 * SERVIÇO, vem do banco (db/seeds/explore_taxonomy.rb) e não passa por aqui.
 */
export type BusinessCategory =
  | 'esportes'
  | 'hospedagem'
  | 'turismo'
  | 'equipamentos'
  | 'performance'
  | 'conteudo'
  | 'experiencias'

interface CategoryCopy {
  heading: string
  body: string
}

/**
 * Título e corpo por categoria — o mesmo convite, falando direto com quem está
 * vendo aquele filtro específico. O dono se vê no título em vez de precisar se
 * reconhecer numa lista de exemplos.
 *
 * Todas seguem o mesmo eixo do card dos carrosséis (CategorySection): a promessa
 * é ser ENCONTRADO por quem já está procurando, não "criar uma página". É
 * superfície de conversão e vai receber tráfego pago — o argumento tem que ser o
 * resultado, não o cadastro.
 *
 * Nada de número de audiência: o diretório é novo, e prometer alcance que o
 * produto ainda não tem queima a marca no primeiro clique.
 *
 * `DEFAULT_COPY` (sem filtro) é quem carrega a lista de exemplos: sem ela,
 * "negócio que vive do mar" sozinho soa vago demais.
 */
const CATEGORY_COPY: Record<BusinessCategory, CategoryCopy> = {
  esportes: {
    heading: 'Sua escola na frente de quem já quer entrar na água',
    body: 'Quem procura aula de surf, kite ou mergulho encontra você aqui — no filtro e no mapa —, e fala direto no seu WhatsApp.',
  },
  hospedagem: {
    heading: 'Sua pousada na frente de quem já está planejando a viagem',
    body: 'Pousada, hotel, surf house ou camp: quem busca onde ficar perto do pico encontra você aqui e fala direto no seu WhatsApp.',
  },
  turismo: {
    heading: 'Sua agência na frente de quem já está montando o roteiro',
    body: 'Surf trip, passeio de barco, guia ou transfer: quem procura encontra você aqui — no filtro e no mapa — e fala direto no seu WhatsApp.',
  },
  equipamentos: {
    heading: 'Sua loja na frente de quem precisa de prancha hoje',
    body: 'Venda, aluguel, shape, pintura ou conserto: quem está procurando encontra você aqui e fala direto no seu WhatsApp.',
  },
  performance: {
    heading: 'Seu trabalho na frente de quem treina para o mar',
    body: 'Academia, preparação física, yoga, fisioterapia ou nutrição: quem quer render mais na água encontra você aqui e fala direto no seu WhatsApp.',
  },
  conteudo: {
    heading: 'Seu trabalho na frente de quem quer registrar a sessão',
    body: 'Foto e vídeo de surf, kite, wing ou SUP: quem procura encontra o seu portfólio aqui e fala direto no seu WhatsApp.',
  },
  experiencias: {
    heading: 'Seu lugar na frente de quem está na praia agora',
    body: 'Beach club, restaurante, trilha, ecoturismo ou evento: quem procura o que fazer encontra você aqui e fala direto no seu WhatsApp.',
  },
}

const DEFAULT_COPY: CategoryCopy = {
  heading: 'Seu negócio na frente de quem já está procurando',
  body: 'Escola, pousada, agência, loja, shaper, fotógrafo — se vive do mar, tem lugar no Explore. Página própria com suas fotos, seus serviços e seu WhatsApp, aparecendo no filtro e no mapa. O cliente fala direto com você.',
}

const FINE_PRINT =
  'Assinatura mensal a partir de R$ 8,90, sem comissão por cliente fechado. Nossa IA monta a página em minutos — você revisa e publica.'

function resolveCopy(category?: BusinessCategory): CategoryCopy {
  return category ? CATEGORY_COPY[category] : DEFAULT_COPY
}

interface CreateYourPageCardProps {
  /** Categoria ativa no Explore quando o convite aparece — muda o título e o
   *  corpo para falar direto com quem tem aquele tipo de negócio. Sem ela (ou
   *  com o filtro "Tudo"), cai na copy genérica, com a lista de exemplos. */
  category?: BusinessCategory
  className?: string
}

/**
 * O bloco fechado, para onde não há texto em volta: o fim da lista do Explore e
 * o pé da seção da landing.
 */
export function CreateYourPageCard({
  category,
  className,
}: CreateYourPageCardProps) {
  const copy = resolveCopy(category)

  return (
    <section
      aria-labelledby="create-your-page-heading"
      className={cn(
        'flex flex-col gap-4 rounded-3xl border border-lp-green/20 bg-gradient-to-br from-[#EFF6FF] to-white p-6 sm:p-8',
        className,
      )}
    >
      <div className="flex items-start gap-4">
        <div className="flex shrink-0 items-center justify-center rounded-2xl border border-lp-blue p-2.5 text-lp-blue">
          <Megaphone className="size-6" strokeWidth={2} aria-hidden="true" />
        </div>

        <div className="flex flex-col gap-2">
          <h2
            id="create-your-page-heading"
            className="text-lg font-bold leading-tight text-gray-900 sm:text-xl"
          >
            {copy.heading}
          </h2>
          <p className="text-sm leading-[1.5] text-gray-600 sm:text-base">
            {copy.body}
          </p>
        </div>
      </div>

      <CreateYourPageButton className="sm:ml-[3.75rem]" />

      <p className="text-xs leading-[1.5] text-gray-500 sm:ml-[3.75rem] sm:text-[0.8125rem]">
        {FINE_PRINT}
      </p>
    </section>
  )
}

/**
 * Fotos que flanqueiam o banner grande, na home. Próprias do convite — não
 * repetem as do "Como funciona" nem as do carrossel de apps, mais acima na
 * mesma página.
 */
const SIDE_PHOTOS = [
  {
    src: '/explore/kitesurf-praia.webp',
    alt: 'Kitesurfista carregando a prancha na praia, com cata-ventos ao fundo',
  },
  {
    src: '/explore/kitesurf-ceu.webp',
    alt: 'Kitesurfista empinando a pipa numa praia cheia de outras pipas no ar',
  },
] as const

/**
 * Versão banner, para a home: foto — texto — foto, no mesmo estilo do
 * carrossel de apps logo acima (painel com o gradiente da marca, cantos bem
 * arredondados). As fotos quebram o que seria só uma caixa de texto — o
 * mesmo argumento do card compacto, com a presença de um banner de verdade.
 *
 * Só para a home: o fim da lista do Explore usa `CreateYourPageCard` — ali não
 * há largura nem fotos para sustentar um banner deste tamanho.
 */
export function CreateYourPageBanner({
  category,
  className,
}: {
  /** Ver `CreateYourPageCardProps.category`. */
  category?: BusinessCategory
  className?: string
}) {
  const copy = resolveCopy(category)

  return (
    <section
      aria-labelledby="create-your-page-heading"
      // No mobile as três peças empilham na ordem do DOM (foto, texto, foto) —
      // é o grid de 1 coluna implícito. No desktop viram 3 colunas lado a lado.
      className={cn(
        'grid gap-4 lg:min-h-[320px] lg:grid-cols-[1fr_1.4fr_1fr] lg:gap-6',
        className,
      )}
    >
      <img
        src={SIDE_PHOTOS[0].src}
        alt={SIDE_PHOTOS[0].alt}
        loading="lazy"
        className="h-56 w-full rounded-[40px] object-cover lg:h-full lg:min-h-64"
      />

      <div
        className="flex flex-col justify-center rounded-[40px] p-8 sm:p-9 lg:p-10"
        style={{ background: PANEL_GRADIENT }}
      >
        <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white">
          <Megaphone className="size-6" strokeWidth={2} aria-hidden="true" />
        </div>

        <h2
          id="create-your-page-heading"
          className="mt-6 text-[1.75rem] font-bold leading-[1.08] tracking-tight text-white sm:text-[2.125rem] lg:text-[2rem]"
        >
          {copy.heading}
        </h2>

        <p className="mt-4 max-w-[30rem] text-[0.9375rem] leading-relaxed text-white/80 sm:text-base">
          {copy.body}
        </p>

        <CreateYourPageButton className="mt-7 bg-white text-lp-blue" />

        <p className="mt-4 max-w-[26rem] text-xs leading-relaxed text-white/60 sm:text-[0.8125rem]">
          {FINE_PRINT}
        </p>
      </div>

      <img
        src={SIDE_PHOTOS[1].src}
        alt={SIDE_PHOTOS[1].alt}
        loading="lazy"
        className="h-56 w-full rounded-[40px] object-cover lg:h-full lg:min-h-64"
      />
    </section>
  )
}
